/**
 * Test Result Correction / Invalidation — pure helpers shared by whatever
 * UI surfaces the Invalidate/Correct actions (Testing History today).
 * Nothing here calls Supabase; these only interpret the shape
 * test_attempts rows already carry (055) once fetched.
 */

/** Canonical reason codes (055's own CHECK constraint) with their
 * human-readable labels — selectors/dropdowns only, per product
 * preference; free text is limited to an always-optional note. */
export const INVALIDATION_REASONS = [
  { value: 'data_entry_error', label: 'Data Entry Error' },
  { value: 'wrong_athlete', label: 'Wrong Athlete' },
  { value: 'wrong_test', label: 'Wrong Test' },
  { value: 'timing_error', label: 'Timing Error' },
  { value: 'equipment_error', label: 'Equipment Error' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'other', label: 'Other' },
];

/** Human-readable label for a stored reason code, or the raw code itself
 * if it's somehow not one of the seven canonical values (never throws,
 * never fabricates a nicer-looking label for an unrecognized value). */
export function invalidationReasonLabel(code) {
  const found = INVALIDATION_REASONS.find(r => r.value === code);
  return found ? found.label : code;
}

/** A correction's replacement always lives in the SAME testing_session
 * as the attempt it corrects (055's own provenance rule) — so finding
 * "what replaced this attempt, if anything" only ever needs to look
 * within that one session's own nested test_attempts, never a
 * cross-session query. Returns null if no replacement exists yet. */
export function findReplacementAttempt(session, attemptId) {
  return (session?.test_attempts || []).find(a => a.replaces_attempt_id === attemptId) || null;
}

/** Whether a session contains ANY correction/invalidation provenance —
 * an invalidated attempt, or a replacement attempt. Mirrors EXACTLY the
 * predicate update_testing_session_with_attempts() (055) now rejects
 * bulk edits on, so the client never offers an action the server would
 * refuse. */
export function sessionHasCorrectionProvenance(session) {
  return (session?.test_attempts || []).some(a => a.status === 'invalidated' || a.replaces_attempt_id);
}

/** Whether Invalidate/Correct actions should ever be offered for this
 * specific attempt, independent of who's viewing:
 * - a 'valid' attempt may be invalidated or corrected
 * - an 'invalidated' attempt with NO replacement yet may still be
 *   corrected (055 section 6 — the "invalidated now, true value learned
 *   later" workflow) but not invalidated again
 * - an 'invalidated' attempt that already has a replacement is fully
 *   resolved — neither action applies */
export function attemptActionAvailability(session, attempt) {
  if (attempt.status === 'valid') {
    return { canInvalidate: true, canCorrect: true };
  }
  const hasReplacement = Boolean(findReplacementAttempt(session, attempt.id));
  return { canInvalidate: false, canCorrect: !hasReplacement };
}
